/**
 * Atributos dos pratos — dietas, picância e tags de sabor (#214, spec
 * §6.5/§16.2). Mesmo contrato de segurança dos alergénios (#325): a IA
 * SUGERE (`status 'suggested'`), o dono CONFIRMA e só o confirmado chega ao
 * cardápio público.
 *
 * Os nomes dos atributos vivem no banco (ui_texts, chaves `diet.<tag>`,
 * semeadas na migração 0105 a partir DESTE arquivo); aqui ficam como fonte
 * única e fallback embutido. Bases = as 14 línguas oficiais (códigos sem
 * região, #419).
 */

import { normalizeLocale } from './i18n.js'

/** Dietas com exibição pública — lista fechada (não inventa tag nova). */
export const DIET_TAGS = [
  'vegetarian',
  'vegan',
  'gluten_free',
  'lactose_free',
  'halal',
  'kosher',
] as const
export type DietTag = (typeof DIET_TAGS)[number]

const DIET_TAG_SET: ReadonlySet<string> = new Set(DIET_TAGS)

/** `true` se `tag` é uma dieta da lista fechada. */
export function isDietTag(tag: unknown): tag is DietTag {
  return typeof tag === 'string' && DIET_TAG_SET.has(tag)
}

/** Picância 0–3 (0 = sem picância; ícone 🌶 repetido `level` vezes). */
export const SPICE_LEVELS = [0, 1, 2, 3] as const
export type SpiceLevel = (typeof SPICE_LEVELS)[number]

/** `true` se `n` é um nível válido de picância (0–3). */
export function isSpiceLevel(n: unknown): n is SpiceLevel {
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 3
}

/** Máx. tags de sabor por produto (a IA sugere ≤ 4; o dono pode até 6). */
export const MAX_FLAVOR_TAGS = 6

/** Tag de sabor válida: minúscula, 2–24 chars, letras (incl. acentos)/números/espaço/hífen. */
const FLAVOR_TAG_RE = /^[\p{L}\p{N}][\p{L}\p{N} -]{1,23}$/u

/** `true` se `tag` tem o formato de tag de sabor aceito. */
export function isFlavorTag(tag: unknown): tag is string {
  if (typeof tag !== 'string') return false
  const normalized = tag.trim().toLowerCase()
  return normalized.length > 0 && FLAVOR_TAG_RE.test(normalized)
}

/** Seis nomes (um por dieta), na ordem de DIET_TAGS. */
type DietNames = [string, string, string, string, string, string]

/** Nomes por base de idioma, na ordem de DIET_TAGS. */
export const DIET_TAG_NAMES: Record<string, DietNames> = {
  pt: ['Vegetariano', 'Vegano', 'Sem glúten', 'Sem lactose', 'Halal', 'Kosher'],
  en: ['Vegetarian', 'Vegan', 'Gluten-free', 'Lactose-free', 'Halal', 'Kosher'],
  es: ['Vegetariano', 'Vegano', 'Sin gluten', 'Sin lactosa', 'Halal', 'Kosher'],
  fr: ['Végétarien', 'Végétalien', 'Sans gluten', 'Sans lactose', 'Halal', 'Casher'],
  de: ['Vegetarisch', 'Vegan', 'Glutenfrei', 'Laktosefrei', 'Halal', 'Koscher'],
  it: ['Vegetariano', 'Vegano', 'Senza glutine', 'Senza lattosio', 'Halal', 'Kosher'],
  nl: ['Vegetarisch', 'Vegan', 'Glutenvrij', 'Lactosevrij', 'Halal', 'Koosjer'],
  zh: ['素食', '纯素', '无麸质', '无乳糖', '清真', '犹太洁食'],
  ja: [
    'ベジタリアン',
    'ヴィーガン',
    'グルテンフリー',
    'ラクトースフリー',
    'ハラール',
    'コーシャー',
  ],
  ko: ['채식', '비건', '글루텐 프리', '락토스 프리', '할랄', '코셔'],
  ru: ['Вегетарианское', 'Веганское', 'Без глютена', 'Без лактозы', 'Халяль', 'Кошер'],
  ar: ['نباتي', 'نباتي صرف', 'خالٍ من الغوتين', 'خالٍ من اللاكتوز', 'حلال', 'كوشير'],
  hi: ['शाकाहारी', 'शुद्ध शाकाहारी', 'ग्लूटेन-मुक्त', 'लैक्टोज़-मुक्त', 'हलाल', 'कोशेर'],
  el: ['Χορτοφαγικό', 'Vegan', 'Χωρίς γλουτένη', 'Χωρίς λακτόζη', 'Χαλάλ', 'Κοσέρ'],
}

/** Nome da dieta no idioma pedido (normalizado para a base; cascata base → en). */
export function dietTagName(tag: DietTag, locale: string | undefined): string {
  if (!locale) return DIET_TAG_NAMES.en![DIET_TAGS.indexOf(tag)] ?? tag
  const base = normalizeLocale(locale)
  const list = DIET_TAG_NAMES[base] ?? DIET_TAG_NAMES.en!
  return list[DIET_TAGS.indexOf(tag)] ?? tag
}
